import React, { useState, useContext } from "react";

import { useNavigate } from "react-router-dom";
import { Box, styled } from "@mui/material";
import { Carousel, ImageUploadModal } from "../../common/commonComps";
import { uploadImage } from "../../common/utils";
import { Context } from "../../common/context";
import { AccountModal } from "../account/accountPieces";
import type { Size, ContextValue, Config, CarouselItem } from "../../types";

const sizes: Record<Size, { h: string; w: string }> = {
    small: { h: `192px`, w: `397px` },
    medium: { h: `368px`, w: `722px` },
    large: { h: `368px`, w: `64%` },
};

const Wrap = styled(Box)<{ size?: Size }>(({ size }) => ({
    maxWidth: size ? sizes[size]?.w : undefined,
    display: `block`,
    minWidth: `300px`,
    minHeight: `200px`,
    cursor: `pointer`,
}));

interface BigCarouselProps {
    size?: Size;
    editable: boolean;
    // Extra props may be spread from AppContext but are unused
    [key: string]: unknown;
}

const BigCarousel = ({ size, editable }: BigCarouselProps): React.ReactElement => {
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [fileLink, setFileLink] = useState<string | undefined>();
    const [file, setFile] = useState<File | undefined>();
    const [link, setLink] = useState<string>(``);
    const cx = useContext(Context) as ContextValue;
    const bigCarousel = cx.getValue
        ? (cx.getValue(`config`) as Config)?.bigCarousel
        : ({ text: `abc`, image: `http://cnn.com/logo` } as CarouselItem); // this is for testing because test-renderer dies on context

    const clearModal = () => {
        setLink(``);
        setFile(undefined);
        setFileLink(undefined);
        setModalOpen(false);
        document.body.style.overflow = `auto`;
    };

    const openModal = () => {
        setModalOpen(true);
        document.body.style.overflow = `hidden`;
    };

    const setImage = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (e.target.files) {
            const f = e.target.files[0];
            setFileLink(URL.createObjectURL(f));
            setFile(f);
        }
    };

    const sendImage = (): void => {
        if (!file) {
            setFile(undefined);
            setFileLink(undefined);
            setModalOpen(false);
            document.body.style.overflow = `auto`;
            cx.setContext({ bigCarousel: { ...bigCarousel, link: link || bigCarousel?.link } });
            setLink(``);
            return;
        }
        uploadImage(file).then(image => {
            setFile(undefined);
            setFileLink(undefined);
            setModalOpen(false);
            document.body.style.overflow = `auto`;
            cx.setContext({ bigCarousel: { ...bigCarousel, image, link: link || bigCarousel?.link } });
            setLink(``);
        });
    };

    return (
        <Wrap
            size={size}
            onClick={() => {
                if (!editable) {
                    if ((bigCarousel?.link || ``).includes(`http`)) {
                        window.open(bigCarousel?.link);
                    } else {
                        navigate(bigCarousel?.link ?? ``);
                    }
                }
            }}
        >
            <AccountModal open={modalOpen}>
                <ImageUploadModal
                    {...{ fileLink, context: bigCarousel, clearModal, link, setLink, sendImage, setImage }}
                />
            </AccountModal>
            <div
                style={{ minWidth: `300px`, minHeight: `200px` }}
                onClick={() => (editable ? openModal() : (() => {})())}
            >
                <Carousel src={bigCarousel?.image ?? ``} />
            </div>
        </Wrap>
    );
};

export default BigCarousel;
